import * as React from "react";
import { cn } from "@/lib/utils";
import { Dot, type DotTone } from "./dot";

export type PillTone = "success" | "warning" | "danger" | "info" | "brand" | "neutral";

const TONE_CLASSES: Record<PillTone, string> = {
  success: "bg-rd-success-50 text-rd-success-700 border-rd-success-500/30",
  warning: "bg-rd-warning-50 text-rd-warning-700 border-rd-warning-500/30",
  danger: "bg-rd-danger-50 text-rd-danger-700 border-rd-danger-500/30",
  info: "bg-rd-info-50 text-rd-info-600 border-rd-info-500/30",
  brand: "bg-brand-50 text-brand-700 border-brand-500/30",
  neutral: "bg-bg-muted-rd text-text-secondary border-border-subtle",
};

export interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: PillTone;
  withDot?: boolean;
  pulse?: boolean;
  icon?: React.ReactNode;
}

export const Pill = React.forwardRef<HTMLSpanElement, PillProps>(
  (
    { tone = "neutral", withDot = false, pulse = false, icon, className, children, ...rest },
    ref
  ) => {
    const dotTone: DotTone = tone === "neutral" ? "neutral" : tone;
    return (
      <span
        ref={ref}
        data-tone={tone}
        className={cn(
          "inline-flex h-[22px] items-center gap-1.5 rounded-full border px-2",
          "whitespace-nowrap text-[11px] font-medium leading-none tracking-[0.02em]",
          TONE_CLASSES[tone],
          className
        )}
        {...rest}
      >
        {withDot && <Dot tone={dotTone} halo={false} pulse={pulse} size={6} />}
        {icon}
        {children}
      </span>
    );
  }
);
Pill.displayName = "Pill";
